import React from 'react';
import { ChevronDown, Search } from 'lucide-react';

/**
 * ONE SET OF APP PRIMITIVES FOR EVERY DASHBOARD SCREEN.
 *
 * The Tracks, Groups and Tables screens were each built from scratch, which is
 * why they read as dated and as three different products:
 *
 *   - Pure Tailwind cool greys (neutral-900/500/200) on a site whose entire
 *     palette is warm. Grey borders against cream is the single loudest tell.
 *   - A separately bordered box per item, in a grid. N items meant N rectangles
 *     at identical weight, so nothing had hierarchy and the screen read as a
 *     filing cabinet.
 *   - An icon in a tinted square on every row, in a different hue per file —
 *     neutral in Tracks, indigo in Groups, teal in Tables. Three accents that
 *     appear nowhere else on the site.
 *   - rounded-lg / rounded-md everywhere, against the site's much larger radii.
 *   - Saturated status dots (emerald / amber) as the only colour on the screen.
 *
 * What replaces it: warm hairlines, ONE panel with hairline-divided rows
 * instead of a grid of boxes, quiet glyphs that only take the accent when a row
 * is pointed at, pill controls, and a single type scale shared by all three
 * screens. No content changed anywhere.
 */

export const INK = '#2B211A';
export const MUTED = '#6B5D52';
export const FAINT = '#8A7B6D';
export const HAIRLINE = '#EFE7DA';
export const ACCENT = '#EA580C';

/** Resting elevation for a surface that sits on the app's cream ground. */
export const SURFACE = 'rounded-[20px] border border-[#EFE7DA] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04)]';

/**
 * Title, one line of explanation, one action. The heading uses the site's
 * display face and tracking rather than a bold sans — an app screen inside a
 * marketing page should look like it belongs to the same company as the page.
 */
export function ScreenHeader({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-start gap-4" data-id="element-2989" data-mp="2q7y3tropuo8b">
      <div className="mr-auto min-w-0" data-id="element-2990" data-mp="2q9iw1ank5v30">
        <h1 className="font-title text-[27px] font-bold leading-tight tracking-[-0.03em] text-[#2B211A]" data-id="element-2991" data-mp="2u9jhvgczl8k3">
          {title}
        </h1>
        {description && <p className="mt-2 max-w-[62ch] text-[14px] leading-relaxed text-[#6B5D52]" data-id="element-2992" data-mp="2z71kyq4botk6">
            {description}
          </p>}
      </div>
      {action}
    </div>;
}

/** The one filled control on a screen. Ink at rest, accent on hover. */
export function PrimaryAction({
  children,
  label
}: {
  children?: React.ReactNode;
  label?: string;
}) {
  return <button type="button" aria-label={label} className="inline-flex flex-none items-center gap-1.5 rounded-full bg-[#2B211A] px-4 py-2 text-[13.5px] font-bold text-white transition-colors duration-200 ease-out hover:bg-[#EA580C]" data-id="element-2993" data-mp="3hzdz0a4c9ja5">
      {children}
    </button>;
}

/** A quiet bordered control. Pill, warm hairline, no grey. */
export function SelectPill({
  value,
  onChange,
  options,
  label,
  prefix
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label: string;
  prefix?: string;
}) {
  return <div className="relative" data-id="element-2994" data-mp="1g95bd78xy6rv">
      <select value={value} onChange={event => onChange(event.target.value)} aria-label={label} className="appearance-none rounded-full border border-[#EFE7DA] bg-white py-2 pl-3.5 pr-9 text-[13px] font-semibold text-[#4A3D33] outline-none transition-colors duration-200 ease-out hover:border-[#DFD3C2] focus:border-[#EA580C]" data-id="element-2995" data-mp="1gc19avksgvgb">
        {options.map(option => <option key={option} value={option} data-id="element-2996" data-mp="1z068rquexu9b">
            {prefix ? `${prefix} · ${option}` : option}
          </option>)}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#A2917F]" data-id="element-2997" data-mp="1u7wnio48tidd" />
    </div>;
}

/** Layout / view switcher. The active segment is ink, not a coloured fill. */
export function Segmented<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly (readonly [T, React.ElementType])[];
}) {
  return <div className="flex flex-none items-center gap-0.5 rounded-full border border-[#EFE7DA] bg-white p-1" data-id="element-2998" data-mp="22fzg50bd9k7y">
      {options.map(([key, Icon]) => <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={value === key} aria-label={`${key} layout`} className={`rounded-full p-1.5 transition-colors duration-200 ease-out ${value === key ? 'bg-[#2B211A] text-white' : 'text-[#A2917F] hover:text-[#2B211A]'}`} data-id="element-2999" data-mp="1sjcwgeus0z7i">
          <Icon size={14} data-id="element-3000" data-mp="2q6vaa43aorid" />
        </button>)}
    </div>;
}
export function SearchField({
  value,
  onChange,
  placeholder,
  label
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return <div className="relative" data-id="element-3001" data-mp="2gg0h0frmsrnf">
      <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A2917F]" data-id="element-3002" data-mp="2gez1nz3l1izw" />
      <input type="search" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-label={label} className="w-full rounded-full border border-[#EFE7DA] bg-white py-2.5 pl-11 pr-4 text-[14px] text-[#2B211A] outline-none transition-colors duration-200 ease-out placeholder:text-[#A2917F] focus:border-[#EA580C]" data-id="element-3003" data-mp="345bjq2sjkw47" />
    </div>;
}

/**
 * A GROUPED LIST, not a grid of boxes. One surface, hairline dividers. This is
 * the single biggest change to how these screens read: a list has one left edge
 * to run your eye down and one border in total, where the old grid drew a
 * rectangle around every item and gave them all the same weight.
 */
export function Panel({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`overflow-hidden ${SURFACE} ${className}`} data-id="element-3004" data-mp="nlr9fmto94uk">{children}</div>;
}

/** One row inside a Panel. */
export function Row({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`group flex items-center gap-3.5 border-b border-[#F5EFE6] px-5 py-3.5 transition-colors duration-200 ease-out last:border-0 hover:bg-[#FDFBF7] ${className}`} data-id="element-3005" data-mp="1uxrsc5hi5qev">
      {children}
    </div>;
}

/**
 * The row's mark. Quiet by default and only accented while the row is pointed
 * at — an always-on tinted square per row turns a list into a wall of badges,
 * which is what made these screens feel busy.
 */
export function Glyph({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="flex size-9 flex-none items-center justify-center rounded-full bg-[#FAF8F4] text-[#8A7B6D] transition-colors duration-200 ease-out group-hover:bg-[#FDF0E6] group-hover:text-[#EA580C]" data-id="element-3006" data-mp="8wcrgam5l91z">
      <Icon size={16} strokeWidth={2.1} data-id="element-3007" data-mp="hwspv5o1hpzz" />
    </span>;
}

/** Row title over one line of supporting detail. */
export function RowText({
  title,
  meta
}: {
  title: string;
  meta: React.ReactNode;
}) {
  return <span className="min-w-0 flex-1" data-id="element-3008" data-mp="3ma7p34uco9rv">
      <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3009" data-mp="3m96d8y5ifj93">{title}</span>
      <span className="mt-0.5 block truncate text-[12.5px] text-[#8A7B6D]" data-id="element-3010" data-mp="3qb19ho8l36g0">{meta}</span>
    </span>;
}

/**
 * Status as a small ring rather than a saturated dot. On a warm screen an
 * emerald or amber disc is the brightest thing present, which put the loudest
 * mark on the least important fact in the row.
 */
export function StatusRing({
  status
}: {
  status: 'active' | 'dormant' | 'archived';
}) {
  const tone = status === 'active' ? 'bg-[#EA580C]' : status === 'dormant' ? 'bg-[#C4B4A2]' : 'bg-[#E4DACB]';
  return <span aria-label={status} title={status} className={`size-[7px] flex-none rounded-full ${tone}`} data-id="element-3011" data-mp="2zwbo3vd6wiir" />;
}

/** A number over its label. Number first — it is what is being compared. */
export function MetaStat({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div data-id="element-3012" data-mp="2m0zn568fxvte">
      <dd className="font-title text-[17px] font-bold leading-none tracking-[-0.01em] text-[#2B211A]" data-id="element-3013" data-mp="2m19fh0gykf29">
        {value}
      </dd>
      <dt className="mt-1 text-[11.5px] text-[#A2917F]" data-id="element-3014" data-mp="2c4d4mo3l7guj">{label}</dt>
    </div>;
}

/** The empty state, in the same warm voice as the rest of the screen. */
export function EmptyState({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={`${SURFACE} px-6 py-14 text-center text-[14px] text-[#8A7B6D]`} data-id="element-3015" data-mp="8urnmmh8ixl4">{children}</div>;
}